import type { OrgFeature } from '@risksmart-app/modules/src/index';
import { useContext } from 'react';

import { FeaturesContext } from './FeaturesContext';

/**
 * Returns the raw org features array from the DB. Prefer the purpose-built
 * hooks over using this directly:
 *
 * - **Module checks** → `useIsModuleEnabled` / `useIsModuleEnabledLazy`
 * - **Feature flag checks** → `useIsFeatureFlagEnabled` / `useIsFeatureFlagEnabledLazy`
 *
 * Direct usage of `features.includes('someFeature')` bypasses the module
 * resolution logic and won't work correctly for module-backed features.
 *
 * ⚠️ Feature-flag divergence (ENG-406 release gate): `useIsFeatureFlagEnabled`
 * now evaluates flag keys through OpenFeature, but the ~13 remaining consumers
 * that read flag keys off this array (e.g. `formConfigRegistry.ts` reading
 * `form_sections`, `ConditionalFieldsProvider`, the custom-datasource pages)
 * still read `Meta.features`. Once per-org OpenFeature targeting is live those
 * two mechanisms can resolve the SAME flag differently in one render. Migrating
 * these consumers is tracked as ENG-593 (the web analog of the ENG-540 backend
 * reader swap); until then, do not add new flag-key reads here — use
 * `useIsFeatureFlagEnabled` so the whole app converges on one source.
 */
export const useFeatures = (): OrgFeature[] => {
  const context = useContext(FeaturesContext);
  if (context === null) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }

  return context;
};
